import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { DataQueryController } from "ui-common";
import "ui-components/skeleton-rect";

import { styles } from "./profile-color-selector.css";

import { profileDataQueryOptions } from "../../queries/profile-data";
import { t } from "../../translations/i18n";

@customElement("profile-color-selector")
export class ProfileColorSelector extends LitElement {
  static override styles = styles;

  profileQuery = new DataQueryController(this, () => profileDataQueryOptions);

  @property({ type: String })
  selectedId: string | undefined;

  @property({ type: String })
  override title = "";

  @property({ type: String })
  primaryColor?: string;

  @property({ type: String })
  secondaryColor?: string;

  @state()
  private _selectedId: string | undefined;

  override connectedCallback() {
    super.connectedCallback();
    this._selectedId = this.selectedId;
  }

  override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has("selectedId")) {
      this._selectedId = this.selectedId;
    }
  }

  private handleCustomColorSelect() {
    this.selectedId = undefined;
    this.dispatchEvent(
      new CustomEvent("custom-color-selected", {
        bubbles: true,
      }),
    );
  }

  private handleColorSelect(id: string) {
    this.selectedId = id;

    const colors = this.profileQuery.result.data?.colors;

    const selectedTheme = colors?.find((color) => color.id === id);

    if (selectedTheme) {
      this.dispatchEvent(
        new CustomEvent("color-selected", {
          detail: { id, colorPair: selectedTheme },
          bubbles: true,
        }),
      );
    }
  }

  protected override render() {
    const displayTitle = this.title || t("profile.profileColor");
    const { data: colorsResponse, isPending, error } = this.profileQuery.result;

    if (isPending) {
      return html`
        <div class="profile-color-selector">
          <h3 class="profile-color-title">${displayTitle}</h3>
          <div class="color-options-container">
            <!-- Screen reader accessible loading text -->
            <span class="sr-only">${t("profile.loadingColors")}</span>
            <!-- Skeleton squares for visual loading state -->
            ${Array(6)
              .fill(0)
              .map(() => html` <skeleton-rect width="40px" height="40px"></skeleton-rect> `)}
          </div>
        </div>
      `;
    }

    if (error) {
      return html`
        <div class="profile-color-selector">
          <h3 class="profile-color-title">${displayTitle}</h3>
          <div class="color-options-container">
            <div>${t("profile.errorLoadingColors")} ${error?.message}</div>
          </div>
        </div>
      `;
    }

    const customColors = colorsResponse?.customColors;

    const displayColors =
      this.primaryColor && this.secondaryColor
        ? { primary: this.primaryColor, secondary: this.secondaryColor }
        : customColors;

    return html`
      <div class="profile-color-selector">
        <h3 class="profile-color-title">${displayTitle}</h3>
        <div class="color-options-container">
          <!-- Custom color button as first option -->
          <button
            class="color-option custom-option ${!this._selectedId ? "active" : ""}"
            @click=${this.handleCustomColorSelect}
            style="${displayColors
              ? `background: linear-gradient(45deg, ${displayColors.primary} 50%, ${displayColors.secondary} 50%)`
              : ""}"
            aria-label=${t("profile.customColorsAriaLabel")}
          >
            <ph-eyedropper size="20"></ph-eyedropper>
          </button>

          <!-- Predefined color options -->
          ${colorsResponse?.colors.map(
            (colorPair, index) => html`
              <button
                class="color-option ${colorPair.id === this._selectedId ? "active" : ""}"
                @click=${() => this.handleColorSelect(colorPair.id)}
                style="background: linear-gradient(45deg, ${colorPair.primary} 50%, ${colorPair.secondary} 50%)"
                aria-label=${t("profile.selectColor", { index: (index + 2).toString() })}
              ></button>
            `,
          )}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "profile-color-selector": ProfileColorSelector;
  }
}
